import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Bell, Send, RefreshCw, Save, Plus, X, AlertTriangle, CheckCircle2,
  ShieldCheck, Mail, Clock, PauseCircle
} from 'lucide-react';

const NAVY = '#0E2A47';
const GREEN = '#8FC93A';

const CATEGORY_LABELS = {
  compensation_approval: 'Compensation approval',
  termination_approval: 'Termination approval',
  budget_approval: 'Budget approval',
  financial_approval: 'Billing approval',
  payroll_approval: 'Payroll approval',
  contract_renewal: 'Contract renewal',
  building_intelligence: 'Building Intelligence verification',
  inspection_followup: 'Inspection follow-up',
  document_review: 'Document review',
  personnel_expiration: 'Document expiration',
  vehicle_compliance: 'Vehicle compliance',
  training_deadline: 'Training deadline',
};

const STATUS_STYLES = {
  Sent: 'bg-green-100 text-green-800 border-green-200',
  Held: 'bg-slate-100 text-slate-700 border-slate-200',
  Failed: 'bg-red-100 text-red-800 border-red-200',
  'Retry Scheduled': 'bg-amber-100 text-amber-800 border-amber-200',
  Pending: 'bg-blue-100 text-blue-800 border-blue-200',
};

const NotificationSettings = () => {
  const { token } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [meta, setMeta] = useState(null);         // { settings, m365, statuses, categories, live_delivery_note }
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [newTestEmail, setNewTestEmail] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, st, dl] = await Promise.all([
        axios.get(`${API}/notifications/settings`, auth),
        axios.get(`${API}/notifications/deliveries/stats`, auth),
        axios.get(`${API}/notifications/deliveries?limit=50`, auth),
      ]);
      setMeta(s.data);
      setSettings(s.data.settings);
      setStats(st.data);
      setDeliveries(dl.data.deliveries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const patch = (p) => setSettings((s) => ({ ...s, ...p }));

  const toggleCategory = (cat) => {
    const cur = settings.disabled_categories || [];
    patch({ disabled_categories: cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat] });
  };

  const setCatRecipients = (cat, value) => {
    const emails = value.split(',').map((x) => x.trim()).filter(Boolean);
    const next = { ...(settings.category_recipients || {}) };
    if (emails.length) next[cat] = emails; else delete next[cat];
    patch({ category_recipients: next });
  };

  const addTestEmail = () => {
    const e = newTestEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Enter a valid email'); return; }
    const cur = settings.test_recipients || [];
    if (cur.includes(e)) { toast.error('Already added'); return; }
    patch({ test_recipients: [...cur, e] });
    setNewTestEmail('');
  };

  const removeTestEmail = (e) =>
    patch({ test_recipients: (settings.test_recipients || []).filter((x) => x !== e) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/notifications/settings`, {
        live_delivery_enabled: settings.live_delivery_enabled,
        default_language: settings.default_language,
        test_recipients: settings.test_recipients,
        disabled_categories: settings.disabled_categories,
        category_recipients: settings.category_recipients,
      }, auth);
      setSettings(r.data.settings);
      toast.success('Notification settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const runScan = async () => {
    setBusy('scan');
    try {
      const r = await axios.post(`${API}/notifications/scan`, {}, auth);
      const d = r.data;
      toast.success(`Scan complete — ${d.scanned} found · ${d.held} held · ${d.sent} sent · ${d.duplicate} duplicate`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally { setBusy(''); }
  };

  const runRetries = async () => {
    setBusy('retry');
    try {
      const r = await axios.post(`${API}/notifications/deliveries/retry`, {}, auth);
      toast.success(`Retries processed — ${r.data.sent} sent · ${r.data.failed} failed · ${r.data.retrying} re-queued`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally { setBusy(''); }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const r = await axios.post(`${API}/notifications/test-send`, {}, auth);
      const st = r.data?.result?.status;
      if (st === 'Sent') toast.success('Test email accepted by Microsoft 365 (202).');
      else toast.message(`Test send recorded as: ${st}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally { setBusy(''); }
  };

  if (loading) {
    return <div className="p-6 text-slate-500" data-testid="notif-loading">Loading notification settings…</div>;
  }
  if (!settings) return null;

  const m365 = meta?.m365 || {};
  const live = !!settings.live_delivery_enabled;

  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* M365 status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: NAVY }}>
            <Mail className="w-5 h-5" /> Microsoft 365 Delivery
          </CardTitle>
        </CardHeader>
        <CardContent>
          {m365.configured ? (
            <div className="flex items-start gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="m365-ok">
              <CheckCircle2 className="w-4 h-4 mt-0.5" />
              <div>Microsoft 365 is configured. Mailbox: <strong>{m365.mailbox}</strong> · Reply-to: {m365.reply_to}</div>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="m365-missing">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                Microsoft 365 is <strong>not fully configured</strong>. Missing: {(m365.missing || []).join(', ') || 'none'}.
                Email cannot be delivered until these credentials are provided. Capture (Held) still works for verification.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Master switch */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: NAVY }}>
            <ShieldCheck className="w-5 h-5" /> Delivery Mode
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={`rounded-lg border p-4 ${live ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="live-delivery-toggle-label">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={live}
                onChange={(e) => patch({ live_delivery_enabled: e.target.checked })}
                data-testid="live-delivery-toggle"
              />
              <span className="font-semibold" style={{ color: NAVY }}>
                Enable LIVE operational delivery
              </span>
            </label>
            <p className="text-sm text-slate-600 mt-2">
              {live
                ? 'LIVE delivery is ON — scanned notifications will be emailed to their real recipients.'
                : 'LIVE delivery is OFF (Preview-safe). Notifications are captured (Held); only designated test recipients can receive controlled test emails.'}
            </p>
            <p className="text-xs text-slate-500 mt-1">{meta?.live_delivery_note}</p>
          </div>

          <div>
            <Label htmlFor="lang">Notification language</Label>
            <select
              id="lang"
              className="w-full border rounded-md px-3 py-2 mt-1"
              value={settings.default_language}
              onChange={(e) => patch({ default_language: e.target.value })}
              data-testid="language-select"
            >
              <option value="bilingual">Bilingual (English + Spanish)</option>
              <option value="en">English only</option>
              <option value="es">Spanish only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Test recipients */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: NAVY }}>
            <Send className="w-5 h-5" /> Designated Test Recipients
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Controlled test emails can be sent to these addresses even while live delivery is OFF.
            Any address not on this list is rejected for test sends.
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="name@tfsi-us.com"
              value={newTestEmail}
              onChange={(e) => setNewTestEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTestEmail())}
              data-testid="test-recipient-input"
            />
            <Button variant="outline" onClick={addTestEmail} data-testid="add-test-recipient-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="test-recipient-list">
            {(settings.test_recipients || []).length === 0 && (
              <span className="text-sm text-slate-400">No test recipients configured.</span>
            )}
            {(settings.test_recipients || []).map((e) => (
              <span key={e} className="inline-flex items-center gap-1 bg-slate-100 border rounded-full px-3 py-1 text-sm" data-testid={`test-recipient-${e}`}>
                {e}
                <button onClick={() => removeTestEmail(e)} className="text-slate-400 hover:text-red-600" data-testid={`remove-test-recipient-${e}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
          <Button
            onClick={sendTest}
            disabled={busy === 'test' || (settings.test_recipients || []).length === 0}
            style={{ background: GREEN, color: NAVY }}
            data-testid="send-test-btn"
          >
            <Send className="w-4 h-4 mr-1" /> {busy === 'test' ? 'Sending…' : 'Send controlled test email'}
          </Button>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: NAVY }}>
            <Bell className="w-5 h-5" /> Reminder Categories &amp; Recipients
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-3">
            Mute a category to stop its notifications. Leave recipients blank to use the mandatory recipients;
            enter comma-separated emails to override for that category.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="category-table">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">Active</th>
                  <th className="py-2">Recipient override (optional)</th>
                </tr>
              </thead>
              <tbody>
                {(meta?.categories || []).map((cat) => {
                  const muted = (settings.disabled_categories || []).includes(cat);
                  const val = (settings.category_recipients?.[cat] || []).join(', ');
                  return (
                    <tr key={cat} className="border-b last:border-0" data-testid={`category-row-${cat}`}>
                      <td className="py-2 pr-3 font-medium" style={{ color: NAVY }}>{CATEGORY_LABELS[cat] || cat}</td>
                      <td className="py-2 pr-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!muted}
                            onChange={() => toggleCategory(cat)}
                            data-testid={`category-active-${cat}`}
                          />
                          <span className={muted ? 'text-slate-400' : 'text-green-700'}>{muted ? 'Muted' : 'On'}</span>
                        </label>
                      </td>
                      <td className="py-2">
                        <Input
                          placeholder="mandatory recipients"
                          defaultValue={val}
                          onBlur={(e) => setCatRecipients(cat, e.target.value)}
                          data-testid={`category-recipients-${cat}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={save} disabled={saving} style={{ background: NAVY, color: 'white' }} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Button variant="outline" onClick={runScan} disabled={busy === 'scan'} data-testid="run-scan-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${busy === 'scan' ? 'animate-spin' : ''}`} /> Run scan now
        </Button>
        <Button variant="outline" onClick={runRetries} disabled={busy === 'retry'} data-testid="run-retries-btn">
          <Clock className="w-4 h-4 mr-1" /> Process due retries
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="delivery-stats">
          {['Pending', 'Sent', 'Failed', 'Retry Scheduled', 'Held'].map((s) => (
            <div key={s} className={`rounded-lg border p-3 ${STATUS_STYLES[s] || 'bg-slate-50'}`} data-testid={`stat-${s.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="text-2xl font-bold">{stats[s] ?? 0}</div>
              <div className="text-xs">{s}</div>
            </div>
          ))}
        </div>
      )}

      {/* Delivery log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: NAVY }}>
            <PauseCircle className="w-5 h-5" /> Recent Notification Deliveries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deliveries-table">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Recipients</th>
                  <th className="py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.length === 0 && (
                  <tr><td colSpan={5} className="py-4 text-slate-400 text-center">No deliveries yet. Run a scan to capture notifications.</td></tr>
                )}
                {deliveries.map((d) => (
                  <tr key={d.id} className="border-b last:border-0 align-top" data-testid={`delivery-row-${d.id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap">{CATEGORY_LABELS[d.category] || d.category}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-xs ${STATUS_STYLES[d.status] || 'bg-slate-100'}`}>
                        {d.status}
                      </span>
                      {d.test_mode && <span className="ml-1 text-xs text-amber-600">TEST</span>}
                    </td>
                    <td className="py-2 pr-3 max-w-xs truncate" title={d.subject}>{d.subject}</td>
                    <td className="py-2 pr-3 text-xs text-slate-600">{(d.recipients || []).join(', ')}</td>
                    <td className="py-2 text-xs text-slate-500 whitespace-nowrap">{(d.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationSettings;
